import { ArrowBack, Downloading, Print } from "@mui/icons-material";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import { generatePDF } from "../../components/order/export_pdf";
import PaymentModal from "../../components/order/PaymentModal";
import { formatCurrencyInCents, formatDate, formatDocument, formatPhone, formatUuid } from '../../helpers/formatters';

function OrderDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [transaction, setTransaction] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [loadingPDF, setLoadingPDF] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchOrderDetails();
    }, [id]);

    const fetchOrderDetails = () => {
        setIsLoading(true);
        api.get(`orders/${id}`)
            .then(response => {
                // A API retorna os dados em response.data.data
                if (response.data && response.data.success && response.data.data) {
                    setOrder(response.data.data);
                    // Buscar transação relacionada ao pedido
                    fetchTransactionByOrderId(response.data.data.id);
                } else {
                    console.error('Estrutura de resposta inesperada:', response.data);
                    setDidNotLoad(true);
                }
            })
            .catch(error => {
                console.error('Erro ao buscar detalhes da venda:', error);
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const fetchTransactionByOrderId = (orderId) => {
        if (!orderId) return;

        api.get(`orders/${orderId}/transaction`)
            .then(response => {
                if (response.data && response.data.success && response.data.data && response.data.data.transaction) {
                    setTransaction(response.data.data.transaction);
                }
            })
            .catch(error => {
                console.error('Erro ao buscar transação relacionada:', error);
                // Não definir didNotLoad como true aqui, pois a transação é opcional
                // A transação pode não existir para alguns pedidos
            });
    };

    const handlePrintReceipt = (order) => {
        setLoadingPDF(true);
        generatePDF(order);
        setTimeout(() => {
            setLoadingPDF(false);
        }, 1000);
    };

    // Função para calcular o custo total dos produtos (assumindo 70% do preço como custo)
    const calculateProductCosts = () => {
        if (!order?.products) return 0;

        return order.products.reduce((total, product) => {
            const productCost = product.cost_price;
            return total + (productCost * product.quantity);
        }, 0);
    };

    // Função para calcular o valor total recebido (valor líquido da transação)
    const calculateNetReceived = () => {
        if (!transaction) return 0;
        return transaction.net_amount || 0;
    };

    // Função para calcular o valor total pago (valor bruto do pedido)
    const calculateTotalPaid = () => {
        if (!order) return 0;
        let total = order.amount || 0;
        if (order.shipping_method && order.shipping_method.cost > 0) {
            total += order.shipping_method.cost;
        }
        if (order.discount && order.discount > 0) {
            total -= order.discount;
        }
        return total;
    };

    // Função para calcular o saldo (recebido - custos)
    const calculateBalance = () => {
        const received = calculateNetReceived();
        const costs = calculateProductCosts();
        return received - costs;
    };

    // Função para calcular a margem de lucro percentual
    const calculateProfitMargin = () => {
        const received = calculateNetReceived();
        const costs = calculateProductCosts();
        if (received === 0) return 0;
        return ((received - costs) / received) * 100;
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    if (!order) {
        return <Loading />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/orders')} to={'/admin/orders'}>
                    Vendas
                </Link>
                <Typography color="text.primary">Detalhes da Venda</Typography>
            </Breadcrumbs>
            <br />

            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                <Typography variant="h4" gutterBottom>
                    Detalhes da Venda #{order.id ? formatUuid(order.id) : 'Carregando...'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/admin/orders')}
                    >
                        Voltar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={loadingPDF ? <Downloading /> : <Print />}
                        onClick={() => handlePrintReceipt(order)}
                        disabled={loadingPDF}
                    >
                        {loadingPDF ? 'Gerando...' : 'Imprimir Comprovante'}
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Informações do Cliente */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Informações do Cliente
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography><strong>Nome:</strong> {order.customer?.name || 'N/A'}</Typography>
                                {order.customer?.email && (
                                    <Typography><strong>Email:</strong> {order.customer.email}</Typography>
                                )}
                                {order.customer?.phone && (
                                    <Typography><strong>Telefone:</strong> {formatPhone(order.customer.phone)}</Typography>
                                )}
                                {order.customer?.document && (
                                    <Typography><strong>Documento:</strong> {formatDocument(order.customer.document)}</Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Informações da Venda */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Informações da Venda
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography><strong>ID:</strong> {order.id ? formatUuid(order.id) : 'N/A'}</Typography>
                                <Typography><strong>Data:</strong> {formatDate(order.created_at)}</Typography>
                                <Typography><strong>Canal de Venda:</strong> {order.sales_channel?.name || 'N/A'}</Typography>
                                <Typography><strong>Status do Pagamento:</strong>
                                    <Chip
                                        label={order.payment_details?.paid ? 'Pago' : 'Pendente'}
                                        color={order.payment_details?.paid ? 'success' : 'warning'}
                                        size="small"
                                        sx={{ ml: 1 }}
                                    />
                                </Typography>
                                <Typography><strong>Conciliação bancária:</strong>
                                    <Chip
                                        label={order.payment_received ? 'Realizada' : 'Pendente'}
                                        color={order.payment_received ? 'success' : 'warning'}
                                        size="small"
                                        sx={{ ml: 1 }}
                                    />
                                </Typography>
                                {order.payment_methods && order.payment_methods.length > 0 && (
                                    <Typography>
                                        <strong>Pagamento:</strong>
                                        {' '}
                                        {order.payment_methods.map((method, index) => {
                                            const parts = [method.name];
                                            if (method.installments > 1) {
                                                parts.push(`${method.installments}x`);
                                            }
                                            if (method.amount) {
                                                parts.push(formatCurrencyInCents(method.amount));
                                            }
                                            return (
                                                <span key={`${method.name}-${index}`}>
                                                    {index > 0 ? ' + ' : ''}
                                                    {parts.join(' ')}
                                                </span>
                                            );
                                        })}
                                    </Typography>
                                )}
                                {(!order.payment_methods || order.payment_methods.length === 0) && order.payment_details?.payment_method && (
                                    <Typography><strong>Método de Pagamento:</strong> {order.payment_details.payment_method}</Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Produtos */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Produtos
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Produto</strong></TableCell>
                                            <TableCell><strong>Quantidade</strong></TableCell>
                                            <TableCell><strong>Preço Unitário</strong></TableCell>
                                            <TableCell><strong>Subtotal</strong></TableCell>
                                            <TableCell><strong>Custo de produção</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {order.products?.map((product, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{product?.name || 'N/A'}</TableCell>
                                                <TableCell>{product?.quantity || 0}</TableCell>
                                                <TableCell>{product?.base_price ? formatCurrencyInCents(product.base_price) : 'N/A'}</TableCell>
                                                <TableCell>{product?.base_price && product?.quantity ? formatCurrencyInCents(product.base_price * product.quantity) : 'N/A'}</TableCell>
                                                <TableCell>{product?.cost_price ? formatCurrencyInCents(product.cost_price) : 'N/A'}</TableCell>
                                            </TableRow>
                                        )) || []}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Informações de Frete e Pagamento */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Informações de Frete e Pagamento
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                {/* Informações de Frete */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Frete</strong>
                                    </Typography>
                                    {order.shipping_method ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Typography><strong>Tipo:</strong> {order.shipping_method.name}</Typography>
                                            {order.shipping_method.description && (
                                                <Typography><strong>Descrição:</strong> {order.shipping_method.description}</Typography>
                                            )}
                                            <Typography><strong>Prazo de Entrega:</strong> {order.shipping_method.delivery_days} dias</Typography>
                                            <Typography><strong>Custo:</strong> {order.shipping_method.cost ? formatCurrencyInCents(order.shipping_method.cost) : 'Grátis'}</Typography>
                                        </Box>
                                    ) : (
                                        <Typography>Retirada no local</Typography>
                                    )}
                                </Grid>

                                {/* Informações de Pagamento */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Pagamento</strong>
                                    </Typography>
                                    {order.payment_methods && order.payment_methods.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {order.payment_methods.map((method, index) => (
                                                <Box key={`${method.name}-${index}`}>
                                                    <Typography>
                                                        <strong>
                                                            Pagamento
                                                            {' '}
                                                            {index + 1}
                                                            :
                                                        </strong>
                                                        {' '}
                                                        {method.name}
                                                        {method.installments > 1 ? ` (${method.installments}x)` : ''}
                                                        {' — '}
                                                        {formatCurrencyInCents(method.amount)}
                                                    </Typography>
                                                    {method.payment_date && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Data: {formatDate(method.payment_date)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography>Informações de pagamento não disponíveis</Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Resumo Financeiro */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Resumo Financeiro
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {/* Valor Pago */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography>Subtotal:</Typography>
                                    <Typography>{order.amount ? formatCurrencyInCents(order.amount) : 'N/A'}</Typography>
                                </Box>
                                {order.shipping_method && order.shipping_method.cost > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>Frete:</Typography>
                                        <Typography>{formatCurrencyInCents(order.shipping_method.cost)}</Typography>
                                    </Box>
                                )}
                                {order.discount && order.discount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>Desconto:</Typography>
                                        <Typography>-{formatCurrencyInCents(order.discount)}</Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography><strong>Valor Pago:</strong></Typography>
                                    <Typography><strong>{formatCurrencyInCents(calculateTotalPaid())}</strong></Typography>
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                {/* Deduções */}
                                {transaction && transaction.deduction > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography>Deduções/Taxas:</Typography>
                                        <Typography color="error.main">-{formatCurrencyInCents(transaction.deduction)}</Typography>
                                    </Box>
                                )}

                                {/* Valor Recebido */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography>Valor Recebido (líquido):</Typography>
                                    <Typography color={transaction ? 'success.main' : 'warning.main'}>
                                        {transaction ? formatCurrencyInCents(calculateNetReceived()) : 'Sem transação'}
                                    </Typography>
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                {/* Custo dos Produtos */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography>Custo dos Produtos:</Typography>
                                    <Typography color="error.main">-{formatCurrencyInCents(calculateProductCosts())}</Typography>
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                {/* Saldo e Margem */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="h6">Saldo:</Typography>
                                    <Typography
                                        variant="h6"
                                        color={calculateBalance() >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {formatCurrencyInCents(calculateBalance())}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Margem de Lucro:</Typography>
                                    <Typography
                                        variant="body2"
                                        color={calculateProfitMargin() >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {calculateProfitMargin().toFixed(1)}%
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Ações */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Ações
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {!order.payment_details?.paid && order.id && (
                                    <PaymentModal id={order.id} />
                                )}
                                <Alert severity="info">
                                    Use o botão "Imprimir Comprovante" no topo da página para gerar o PDF da venda.
                                </Alert>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </div>
    );
}

export default OrderDetailsPage;
